/**
 * pg-range-filter
 *
 * Dual-handle range slider for discrete-valued product metafield facets
 * (Play Time, Player Count, Recommended Age).
 *
 * Shopify's storefront filter engine returns these metafields as LIST filters --
 * it has no range operator for them (only price supports .gte/.lte). So the
 * slider is a presentation layer: the handles pick a contiguous window over the
 * sorted list of available values, and we tick the matching hidden checkboxes.
 * Those checkboxes live inside the facets <form>, so Horizon's existing
 * FacetsFormComponent picks them up via FormData and Shopify ORs them together.
 *
 * Selecting the full range unchecks everything, which drops the param entirely
 * and reads as "no filter applied".
 */

const UNIT_FORMATTERS = {
  minutes: {
    one: (v) => `${v} min`,
    range: (lo, hi) => `${lo}–${hi} min`,
  },
  players: {
    one: (v) => (v === 1 ? '1 player' : `${v} players`),
    range: (lo, hi) => `${lo}–${hi} players`,
  },
  age: {
    one: (v) => (v === 0 ? 'All ages' : `Ages ${v}+`),
    range: (lo, hi) => (lo === 0 ? `All ages – ${hi}+` : `Ages ${lo}–${hi}`),
  },
};

class PgRangeFilter extends HTMLElement {
  connectedCallback() {
    if (this.dataset.pgReady === 'true') return;

    this.values = JSON.parse(this.dataset.values || '[]').map(Number);
    if (this.values.length === 0) return;

    this.unit = UNIT_FORMATTERS[this.dataset.unit] || UNIT_FORMATTERS.minutes;
    this.minInput = this.querySelector('[data-pg-role="min"]');
    this.maxInput = this.querySelector('[data-pg-role="max"]');
    this.readout = this.querySelector('[data-pg-role="readout"]');
    this.boxes = Array.from(this.querySelectorAll('[data-pg-role="box"]'));
    this.trackFill = this.querySelector('[data-pg-role="fill"]');

    if (!this.minInput || !this.maxInput) return;

    // `input` = live feedback while dragging (no network). `change` = commit.
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onCommit);

    this.#render();
    this.dataset.pgReady = 'true';
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onCommit);
  }

  get #lo() {
    return Math.min(Number(this.minInput.value), Number(this.maxInput.value));
  }

  get #hi() {
    return Math.max(Number(this.minInput.value), Number(this.maxInput.value));
  }

  #onInput = (event) => {
    if (event.target !== this.minInput && event.target !== this.maxInput) return;
    // Keep handles from crossing over one another.
    if (Number(this.minInput.value) > Number(this.maxInput.value)) {
      if (event.target === this.minInput) this.maxInput.value = this.minInput.value;
      else this.minInput.value = this.maxInput.value;
    }
    this.#render();
    event.stopPropagation();
  };

  #onCommit = (event) => {
    if (event.target !== this.minInput && event.target !== this.maxInput) return;
    // Don't let the raw slider `change` bubble to facet-inputs-component; we
    // sync the checkboxes first and then trigger the update ourselves.
    event.stopPropagation();

    this.#syncCheckboxes();

    const form = this.closest('facets-form-component');
    if (form && typeof form.updateFilters === 'function') form.updateFilters();
  };

  #syncCheckboxes() {
    const lo = this.#lo;
    const hi = this.#hi;
    const isFullRange = lo === 0 && hi === this.values.length - 1;

    this.boxes.forEach((box, index) => {
      box.checked = !isFullRange && index >= lo && index <= hi;
    });
  }

  #render() {
    const lo = this.#lo;
    const hi = this.#hi;
    const loVal = this.values[lo];
    const hiVal = this.values[hi];
    const isFullRange = lo === 0 && hi === this.values.length - 1;

    if (this.readout) {
      if (isFullRange) this.readout.textContent = 'Any';
      else if (lo === hi) this.readout.textContent = this.unit.one(loVal);
      else this.readout.textContent = this.unit.range(loVal, hiVal);
    }

    if (this.trackFill) {
      const span = Math.max(this.values.length - 1, 1);
      this.trackFill.style.insetInlineStart = `${(lo / span) * 100}%`;
      this.trackFill.style.insetInlineEnd = `${100 - (hi / span) * 100}%`;
    }

    this.dataset.pgActive = isFullRange ? 'false' : 'true';
  }
}

if (!customElements.get('pg-range-filter')) {
  customElements.define('pg-range-filter', PgRangeFilter);
}
